import { Navigate, Outlet, useLocation } from "react-router";
import { ApiError } from "@studyward/shared";
import { supabase } from "../lib/api";
import { useProfile } from "../lib/queries";
import { useSession } from "../lib/session";
import { AppShell } from "./AppShell";
import { ErrorMessage, FullPageLoading } from "./ui";

/** Signed-out pages (sign in / sign up): a signed-in visitor goes straight on, back to where they were headed. */
export function PublicOnly() {
  const session = useSession();
  const location = useLocation();
  if (session.status === "loading") return <FullPageLoading />;
  if (session.status === "signed_in") {
    const next = new URLSearchParams(location.search).get("next");
    return <Navigate to={next?.startsWith("/") && !next.startsWith("//") ? next : "/dashboard"} replace />;
  }
  return <Outlet />;
}

/** Any signed-in page. Signed-out visitors go to sign in and come back afterwards. */
export function RequireAuth() {
  const session = useSession();
  const location = useLocation();
  if (session.status === "loading") return <FullPageLoading />;
  if (session.status === "signed_out") return <Navigate to={`/signin?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return <Outlet />;
}

/** The app proper: needs finished setup (§8.2), then renders inside the sidebar shell. */
export function RequireSetup() {
  const profile = useProfile();
  if (profile.isPending) return <FullPageLoading />;
  if (profile.isError) {
    if (profile.error instanceof ApiError && profile.error.code === "unauthorized") {
      void supabase.auth.signOut();
      return <Navigate to="/signin" replace />;
    }
    return (
      <div className="mx-auto max-w-md px-4 py-16">
        <ErrorMessage message={profile.error.message} onRetry={() => void profile.refetch()} />
      </div>
    );
  }
  if (!profile.data.onboarding_completed) return <Navigate to="/onboarding" replace />;
  return (
    <AppShell>
      <Outlet />
    </AppShell>
  );
}
